import React, { useState, useEffect, useRef } from "react";

export default function SectionNavScroller({ sections = [] }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || "");
  const [isScrolling, setIsScrolling] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const scrollTimeoutRef = useRef(null);

  const updateActiveSection = () => {
    const scrollPos = window.scrollY + window.innerHeight * 0.35;
    let current = sections[0]?.id;

    for (let i = 0; i < sections.length; i++) {
      const el = document.getElementById(sections[i].id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset;
        if (scrollPos >= top) {
          current = sections[i].id;
        }
      }
    }
    if (current) setActiveId(current);
  };

  useEffect(() => {
    updateActiveSection();

    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 2500);

      updateActiveSection();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -72;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (!sections || sections.length === 0) return null;

  const isVisible = isScrolling || isHovered;

  return (
    <aside
      aria-label="Section quick navigation"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`fixed right-3 sm:right-6 top-1/2 -translate-y-1/2 z-[1001] transition-all duration-700 ease-out select-none ${
        isVisible
          ? "opacity-100 translate-x-0 pointer-events-auto"
          : "opacity-0 translate-x-6 pointer-events-none"
      }`}
    >
      <div className="flex flex-col items-end gap-1.5 p-2 rounded-2xl bg-white/95 backdrop-blur-md border border-[#E7E2D6] shadow-2xl">
        <div className="text-[9px] font-mono uppercase tracking-widest text-[#C5A880] font-bold px-2 py-0.5 text-right">
          On This Page
        </div>

        {sections.map((s) => {
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollTo(s.id)}
              title={`Navigate to ${s.label}`}
              className={`group flex items-center gap-2 rounded-xl transition-all duration-300 text-left ${
                isActive
                  ? "bg-[#0A1128] text-white px-3.5 py-2 shadow-lg border border-[#C5A880]/60 ring-2 ring-[#C5A880]/20 scale-105"
                  : "bg-white/90 hover:bg-[#F5F3EE] text-slate-600 hover:text-[#0A1128] px-3 py-1.5 shadow-2xs border border-[#E7E2D6] hover:border-[#C5A880] backdrop-blur-xs hover:scale-102"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${
                  isActive
                    ? "bg-[#C5A880] animate-pulse"
                    : "bg-slate-300 group-hover:bg-[#C5A880]"
                }`}
              />
              <span
                className={`font-mono text-[10px] shrink-0 ${
                  isActive ? "text-[#C5A880] font-bold" : "text-slate-400 group-hover:text-slate-600"
                }`}
              >
                {s.num}
              </span>
              <span
                className={`text-xs whitespace-nowrap transition-all ${
                  isActive
                    ? "font-semibold text-white"
                    : "font-medium text-slate-600 group-hover:text-[#0A1128]"
                }`}
              >
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
